import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { TENANT_ID } from './tenant';

export function normalizeLocale(value) {
    return String(value || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

const EN = {
    'nav.dashboard': 'Dashboard',
    'nav.newPolicy': 'New Policy',
    'nav.customers': 'Customers',
    'nav.policies': 'Policies',
    'nav.renewals': 'Renewals',
    'nav.claims': 'Claims',
    'nav.approvals': 'Approvals',
    'nav.graWmf': 'GRA / CBG',
    'nav.ussd': 'USSD verify',
    'nav.import': 'Import',
    'nav.team': 'Team',
    'nav.settings': 'Logo & payments',
    'layout.businessDashboard': 'Business Dashboard',
    'layout.tagline': 'Protecting Your Future',
    'layout.connected': 'Connected',
    'layout.quickActions': 'Quick Actions:',
    'layout.logout': 'Logout',
    'layout.workspace': 'Workspace',
    'common.signIn': 'Sign in',
    'common.signOut': 'Log out',
    'common.save': 'Save',
    'common.suspend': 'Suspend',
    'common.cancel': 'Cancel',
    'common.email': 'Email Address',
    'common.password': 'Password',
    'common.loading': 'Loading...',
    'common.print': 'Print certificate',
    'common.check': 'Check',
    'common.search': 'Search',
    'login.title': 'Staff login',
    'login.mfaTitle': 'SMS Token Verification',
    'login.blurb': 'Brokers and department users sign in to {company}.',
    'login.signIn': 'Sign In',
    'login.verifying': 'Verifying...',
    'login.authorizing': 'Authorizing...',
    'login.verifyPin': 'Verify Pin',
    'login.policyholderPortal': 'Policyholder portal',
    'login.demoEmails': 'Demo staff emails stay @pulse-test.com — those are login credentials, not the company name.',
    'login.disabled': 'This staff login is disabled. The insurance account may be suspended or closed. Contact Pulse Insurance.',
    'clientLogin.title': 'Client portal',
    'clientLogin.blurb': 'Sign in with the mobile number and PIN on your policy documents. You stay signed in on this phone until you log out.',
    'clientLogin.phone': 'Phone number',
    'clientLogin.pin': 'PIN code',
    'clientLogin.pinPlaceholder': '4-digit PIN',
    'clientLogin.signingIn': 'Signing in...',
    'clientLogin.policeHint': 'Show your QR to police after you sign in. They do not need your PIN.',
    'clientLogin.staffLogin': 'Staff login',
    'clientLogin.noPolicy': 'No policy found for that phone number and PIN.',
    'clientLogin.disabled': 'The client portal is not enabled for this insurer. Please visit the office or call your agent.',
    'police.title': 'POLICE VERIFICATION',
    'police.subtitle': 'Scan the QR or type the plate · Pulse Insurance The Gambia',
    'police.searchLabel': 'Search by registration number',
    'police.check': 'Check',
    'police.checking': 'Checking cover…',
    'police.coverInForce': 'COVER IN FORCE',
    'police.notInForce': 'NOT IN FORCE',
    'police.plateNotOnCert': 'Plate {plate} is NOT on a Pulse digital certificate.',
    'police.clientPortal': 'Client portal',
    'police.holderLine': '{name} · plate {plate} · {policy}',
    'police.toDate': ' · to {date}',
    'client.welcome': 'Welcome{name}',
    'client.portalBlurb': 'This is your {company} client portal. Open a certificate and show the QR to police.',
    'client.signedIn': 'Signed in',
    'client.logOut': 'Log out',
    'client.certificates': 'My certificates',
    'client.opening': 'Opening your portal…',
    'client.noPolicies': 'No policies on this profile yet.',
    'client.savePrint': 'Save / print certificate',
    'client.pending': '{count} file(s) are with {company} for approval. Your approved certificate appears here after the company stamps it.',
    'policy.captured': 'Policy captured',
    'policy.newClientFile': 'New client file',
    'policy.backDashboard': 'Back to dashboard',
    'policy.clientPortalLine': 'Client portal: /client/login · phone {phone} · PIN {pin}',
    'policy.headingClient': '1. Client details',
    'policy.headingChoose': '2. Choose the policy the client needs',
    'policy.chooseHint': 'Pick a line of business, then the product.',
    'policy.headingVehicle': '3. Vehicle details',
    'policy.headingLife': '3. Life & savings details',
    'policy.headingProperty': '3. Property / asset details',
    'policy.headingMedical': '3. Medical details',
    'policy.headingPremium': '4. Premium & issue',
    'policy.newClient': 'New client — fill the form below',
    'policy.fullName': 'Full name',
    'policy.nationalId': 'National ID / Passport',
    'policy.mobile': 'Mobile number',
    'policy.email': 'Email',
    'policy.address': 'Address',
    'policy.occupation': 'Occupation',
    'policy.dob': 'Date of birth',
    'policy.gender': 'Gender',
    'policy.select': 'Select',
    'policy.female': 'Female',
    'policy.male': 'Male',
    'policy.other': 'Other',
    'policy.pin': 'Policy lookup PIN (4 digits)',
    'policy.suggest': 'Suggest',
    'policy.regNumber': 'Registration number',
    'policy.make': 'Make',
    'policy.model': 'Model',
    'policy.year': 'Year',
    'policy.vin': 'VIN / chassis number',
    'policy.declaredValue': 'Declared value (GMD)',
    'policy.use': 'Use',
    'policy.private': 'Private',
    'policy.taxi': 'Taxi / commercial',
    'policy.goods': 'Goods vehicle',
    'policy.ecowas': 'Include ECOWAS Brown Card',
    'policy.paymentMethod': 'Payment method',
    'policy.notes': 'Underwriter notes',
    'policy.nrp': 'NRP',
    'policy.wmf': 'WMF (10%)',
    'policy.gra': 'GRA tax',
    'policy.gross': 'Gross premium',
    'policy.calculate': 'Calculate premium',
    'policy.issue': 'Issue policy file',
    'policy.saving': 'Saving...',
    'policy.printCert': 'Print certificate',
    'renewals.loading': 'Loading renewals...',
    'renewals.intro': 'Policies expiring in 30 days, 7 days, or already lapsed. WhatsApp / SMS the client. Default is cash at the office; wallets to {wave} if enabled.',
    'renewals.introCash': 'Policies expiring in 30 days, 7 days, or already lapsed. WhatsApp / SMS the client, collect cash at the office, then renew.',
    'renewals.overdue': 'Overdue / lapsed',
    'renewals.week': 'Expires within 7 days',
    'renewals.month': 'Expires within 30 days',
    'renewals.none': 'None.',
    'renewals.expires': 'Expires {date}',
    'renewals.overdueDays': '{days} days overdue',
    'renewals.daysLeft': '{days} days left',
    'renewals.confirm': 'Take payment & renew 12 months',
    'renewals.enterConfirm': 'Enter the Wave / wallet confirmation first.',
    'settings.loading': 'Loading settings...',
    'settings.title': 'Company branding & payments',
    'settings.intro': 'Upload the {company} logo and the pay-to numbers for wallets Pulse enabled for you. Default payment is cash at the office. Pulse Pay (if on): {wave}.',
    'settings.introCash': 'Upload the company logo. Premium is collected cash at the office. Pulse super admin can switch on optional wallets later.',
    'settings.logo': 'Logo',
    'settings.noLogo': 'No logo uploaded yet.',
    'settings.wallets': 'Wallet payments',
    'settings.walletsHint': 'Only rails Pulse enabled for this company. Clients send premium to these numbers. Cash at the office stays the default.',
    'settings.walletsDisabled': 'Wallet rails are not enabled for this company. Cash at the office is the default. Pulse super admin can switch on Wave, Orange Money, QMoney, or Pulse Pay.',
    'settings.walletsSaved': 'Payment settings saved.',
    'settings.walletsFailed': 'Could not save payment settings.',
    'modules.disabled': 'This module is not enabled for your company. Ask Pulse Insurance to switch it on.',
    'settings.pulsePay': 'Pulse Pay number',
    'settings.wave': 'Wave number',
    'settings.orange': 'Orange Money',
    'settings.qmoney': 'QMoney',
    'settings.commission': 'Broker commission %',
    'settings.saveWallets': 'Save wallets',
    'settings.adminOnly': 'Only the company IT admin can upload the business logo.',
    'settings.licenceNote': 'Licence and billing are managed by Pulse Insurance (Pulse super admin only). Company IT admins manage logo, payment wallets, and department users. Account suspension or closure is also Pulse-only.',
    'gra.loading': 'Loading returns...',
    'gra.intro': 'WMF (wakala), GRA tax, and ICC-style written premium for {company}. Pulse Pay / Wave collections go to {wave}.',
    'gra.downloadCsv': 'Download CSV',
    'gra.printPack': 'Print pack',
    'gra.restricted': 'Finance, audit, managers, and IT admin can open the month-end pack.',
    'gra.cbgNote': 'CBG Insurance Regulations 2005: Form C (life certificate/return), Form D (non-life), Forms E/F (returns, accounts, intermediaries). These are working CSV/Excel packs from the Pulse book — not a invented official XML. GRA ITAS pack is for the officer to file or rekey.',
    'gra.formC': 'CBG Form C (life)',
    'gra.formD': 'CBG Form D (non-life)',
    'gra.formE': 'CBG Form E (collections)',
    'gra.formF': 'CBG Form F (intermediaries)',
    'gra.itas': 'GRA ITAS pack',
    'gra.pulsePay': 'Pulse Pay collected',
    'gra.claimsMonth': 'Claims filed (month)',
    'claims.loading': 'Loading claims...',
    'claims.intro': 'File FNOL and move claims open → investigating → approved / rejected / paid.',
    'claims.new': 'New claim',
    'claims.policySearch': 'Policy search',
    'claims.policySearchPh': 'Name, plate, or policy number',
    'claims.pickPolicy': 'Select a policy',
    'claims.incidentDate': 'Incident date',
    'claims.place': 'Place',
    'claims.placePh': 'e.g. Westfield junction',
    'claims.description': 'What happened',
    'claims.estimate': 'Estimated amount (GMD)',
    'claims.photos': 'Photos',
    'claims.photosHint': 'Optional. If Storage is not provisioned, the claim still files without files.',
    'claims.file': 'File claim',
    'claims.filed': 'Claim filed',
    'claims.fileFailed': 'Could not file this claim.',
    'claims.updateFailed': 'Could not update status.',
    'claims.photoSkip': 'Photos were skipped (Storage not available).',
    'claims.id': 'Claim ID',
    'claims.insured': 'Insured party',
    'claims.line': 'Asset line',
    'claims.reserve': 'Reserve',
    'claims.status': 'Status',
    'claims.none': 'No claims yet.',
    'claims.move': 'Move to',
    'claims.st.open': 'open',
    'claims.st.investigating': 'investigating',
    'claims.st.approved': 'approved',
    'claims.st.rejected': 'rejected',
    'claims.st.paid': 'paid',
    'client.fnolTitle': 'Report a claim (FNOL)',
    'client.fnolBlurb': 'Describe the incident on your policy. Photos are optional.',
    'client.fnolPolicy': 'Policy',
    'client.fnolPick': 'Choose a policy',
    'client.fnolDesc': 'What happened',
    'client.fnolPhoto': 'Photo (optional)',
    'client.fnolSubmit': 'Submit claim',
    'client.fnolFiled': 'Claim {num} filed. Claims will contact you.',
    'client.fnolFailed': 'Could not file this claim.',
    'client.fnolPhotoSkip': 'Photo skipped — claim can still be filed.',
    'ussd.title': 'USSD plate / policy verify',
    'ussd.intro': 'Demo of *920# for Africell, QCell, and Gamcel. A live shortcode needs a carrier contract. This uses the same plate index as police verify.',
    'ussd.entryPh': 'Plate or policy number',
    'ussd.send': 'Send',
    'ussd.webhook': 'Telco webhook URL',
    'ussd.webhookHint': 'POST Africa’s Talking–style sessionId, phoneNumber, text, serviceCode. Header x-webhook-secret (same env as mobile-money webhook). Internet scrapers without the secret are rejected.',
    'superadmin.language': 'Language / Langue',
    'superadmin.languageHelp': 'Staff, client portal, and police verification for this company. Super admin portal stays in English.',
    'superadmin.english': 'English',
    'superadmin.french': 'Français',
    'superadmin.languageSaved': 'Language set to {lang} for {company}. Company staff and clients see it after they reload.',
    'cert.org': 'Association of Insurers · The Gambia',
    'cert.official': 'Official digital copy · present to police on request',
    'cert.contact': 'Show this certificate to police. They scan the QR to verify.',
    'cert.plateNo': 'PLATE NO.',
    'cert.type': 'TYPE',
    'cert.make': 'MAKE',
    'cert.holder': 'HOLDER',
    'cert.nationalId': 'NATIONAL ID',
    'cert.vehicle': 'VEHICLE',
    'cert.vin': 'VIN',
    'cert.policyNo': 'POLICY NO.',
    'cert.validFrom': 'VALID FROM',
    'cert.to': 'to',
    'cert.attestationNo': 'Attestation no. {num}',
    'cert.securityKey': 'SECURITY KEY',
    'cert.cover': 'COVER',
    'cert.ecowas': 'ECOWAS',
    'cert.brownCard': 'Brown Card included',
    'cert.dateOfIssue': 'DATE OF ISSUE {date}',
    'cert.attestationTitle': 'INSURANCE ATTESTATION',
    'cert.coverConfirmed': 'COVER CONFIRMED',
    'cert.preparingQr': 'Preparing QR…',
    'cert.plate': 'Plate {plate}',
    'cert.expires': 'Expires {date}',
    'cert.expired': 'EXPIRED',
    'cert.valid': 'VALID',
    'cert.watermark': '{company} · THE GAMBIA',
    'cert.gambiaBand': '{mark} · THE GAMBIA'
};

const FR = {
    'nav.dashboard': 'Tableau de bord',
    'nav.newPolicy': 'Nouvelle police',
    'nav.customers': 'Clients',
    'nav.policies': 'Polices',
    'nav.renewals': 'Renouvellements',
    'nav.claims': 'Sinistres',
    'nav.approvals': 'Validations',
    'nav.graWmf': 'GRA / CBG',
    'nav.ussd': 'Vérif. USSD',
    'nav.import': 'Import',
    'nav.team': 'Équipe',
    'nav.settings': 'Logo et paiements',
    'layout.businessDashboard': 'Tableau de bord',
    'layout.tagline': 'Protéger votre avenir',
    'layout.connected': 'Connecté',
    'layout.quickActions': 'Actions rapides :',
    'layout.logout': 'Déconnexion',
    'layout.workspace': 'Espace de travail',
    'common.signIn': 'Se connecter',
    'common.signOut': 'Se déconnecter',
    'common.save': 'Enregistrer',
    'common.suspend': 'Suspendre',
    'common.cancel': 'Annuler',
    'common.email': 'Adresse e-mail',
    'common.password': 'Mot de passe',
    'common.loading': 'Chargement...',
    'common.print': 'Imprimer le certificat',
    'common.check': 'Vérifier',
    'common.search': 'Rechercher',
    'login.title': 'Connexion personnel',
    'login.mfaTitle': 'Vérification par SMS',
    'login.blurb': 'Courtiers et services se connectent à {company}.',
    'login.signIn': 'Se connecter',
    'login.verifying': 'Vérification...',
    'login.authorizing': 'Autorisation...',
    'login.verifyPin': 'Vérifier le code',
    'login.policyholderPortal': 'Espace assuré',
    'login.demoEmails': 'Les e-mails de démo restent @pulse-test.com — ce sont des identifiants de connexion, pas le nom de la compagnie.',
    'login.disabled': 'Ce compte personnel est désactivé. Le compte d’assurance peut être suspendu ou clôturé. Contactez Pulse Insurance.',
    'clientLogin.title': 'Espace client',
    'clientLogin.blurb': 'Connectez-vous avec le numéro de mobile et le code PIN de vos documents. Vous restez connecté sur ce téléphone jusqu’à la déconnexion.',
    'clientLogin.phone': 'Numéro de téléphone',
    'clientLogin.pin': 'Code PIN',
    'clientLogin.pinPlaceholder': 'PIN à 4 chiffres',
    'clientLogin.signingIn': 'Connexion...',
    'clientLogin.policeHint': 'Montrez votre QR à la police après connexion. Ils n’ont pas besoin de votre PIN.',
    'clientLogin.staffLogin': 'Connexion personnel',
    'clientLogin.noPolicy': 'Aucune police trouvée pour ce numéro et ce PIN.',
    'clientLogin.disabled': 'L’espace client n’est pas activé pour cet assureur. Rendez-vous au bureau ou appelez votre agent.',
    'police.title': 'VÉRIFICATION POLICE',
    'police.subtitle': 'Scannez le QR ou saisissez la plaque · Pulse Insurance Gambie',
    'police.searchLabel': 'Recherche par immatriculation',
    'police.check': 'Vérifier',
    'police.checking': 'Vérification de la couverture…',
    'police.coverInForce': 'COUVERTURE EN VIGUEUR',
    'police.notInForce': 'NON EN VIGUEUR',
    'police.plateNotOnCert': 'La plaque {plate} n’est PAS sur un certificat numérique Pulse.',
    'police.clientPortal': 'Espace client',
    'police.holderLine': '{name} · plaque {plate} · {policy}',
    'police.toDate': ' · jusqu’au {date}',
    'client.welcome': 'Bienvenue{name}',
    'client.portalBlurb': 'Ceci est votre espace client {company}. Ouvrez un certificat et montrez le QR à la police.',
    'client.signedIn': 'Connecté',
    'client.logOut': 'Se déconnecter',
    'client.certificates': 'Mes certificats',
    'client.opening': 'Ouverture de votre espace…',
    'client.noPolicies': 'Aucune police sur ce profil pour le moment.',
    'client.savePrint': 'Enregistrer / imprimer le certificat',
    'client.pending': '{count} dossier(s) chez {company} en attente de validation. Le certificat approuvé apparaît ici après le cachet.',
    'policy.captured': 'Police enregistrée',
    'policy.newClientFile': 'Nouveau dossier client',
    'policy.backDashboard': 'Retour au tableau de bord',
    'policy.clientPortalLine': 'Espace client : /client/login · tél. {phone} · PIN {pin}',
    'policy.headingClient': '1. Coordonnées du client',
    'policy.headingChoose': '2. Choisir la police dont le client a besoin',
    'policy.chooseHint': 'Choisissez une branche, puis le produit.',
    'policy.headingVehicle': '3. Détails du véhicule',
    'policy.headingLife': '3. Détails vie et épargne',
    'policy.headingProperty': '3. Détails du bien / actif',
    'policy.headingMedical': '3. Détails médicaux',
    'policy.headingPremium': '4. Prime et émission',
    'policy.newClient': 'Nouveau client — remplir le formulaire ci-dessous',
    'policy.fullName': 'Nom complet',
    'policy.nationalId': 'CNI / Passeport',
    'policy.mobile': 'Numéro de mobile',
    'policy.email': 'E-mail',
    'policy.address': 'Adresse',
    'policy.occupation': 'Profession',
    'policy.dob': 'Date de naissance',
    'policy.gender': 'Genre',
    'policy.select': 'Sélectionner',
    'policy.female': 'Femme',
    'policy.male': 'Homme',
    'policy.other': 'Autre',
    'policy.pin': 'PIN de consultation (4 chiffres)',
    'policy.suggest': 'Suggérer',
    'policy.regNumber': 'Immatriculation',
    'policy.make': 'Marque',
    'policy.model': 'Modèle',
    'policy.year': 'Année',
    'policy.vin': 'VIN / n° de châssis',
    'policy.declaredValue': 'Valeur déclarée (GMD)',
    'policy.use': 'Usage',
    'policy.private': 'Privé',
    'policy.taxi': 'Taxi / commercial',
    'policy.goods': 'Véhicule de marchandises',
    'policy.ecowas': 'Inclure la Carte Brune CEDEAO',
    'policy.paymentMethod': 'Mode de paiement',
    'policy.notes': 'Notes souscripteur',
    'policy.nrp': 'PNA',
    'policy.wmf': 'WMF (10 %)',
    'policy.gra': 'Taxe GRA',
    'policy.gross': 'Prime brute',
    'policy.calculate': 'Calculer la prime',
    'policy.issue': 'Émettre la police',
    'policy.saving': 'Enregistrement...',
    'policy.printCert': 'Imprimer le certificat',
    'renewals.loading': 'Chargement des renouvellements...',
    'renewals.intro': 'Polices expirant dans 30 jours, 7 jours, ou déjà échues. WhatsApp / SMS au client. Par défaut : espèces au bureau ; wallets vers {wave} si activés.',
    'renewals.introCash': 'Polices expirant dans 30 jours, 7 jours, ou déjà échues. WhatsApp / SMS au client, encaisser les espèces au bureau, puis renouveler.',
    'renewals.overdue': 'Échues / caduques',
    'renewals.week': 'Expire dans 7 jours',
    'renewals.month': 'Expire dans 30 jours',
    'renewals.none': 'Aucune.',
    'renewals.expires': 'Expire le {date}',
    'renewals.overdueDays': '{days} jours de retard',
    'renewals.daysLeft': '{days} jours restants',
    'renewals.confirm': 'Encaisser et renouveler 12 mois',
    'renewals.enterConfirm': 'Saisissez d’abord la confirmation Wave / wallet.',
    'settings.loading': 'Chargement des paramètres...',
    'settings.title': 'Image de marque et paiements',
    'settings.intro': 'Téléversez le logo de {company} et les numéros des wallets activés par Pulse. Paiement par défaut : espèces au bureau. Pulse Pay (si activé) : {wave}.',
    'settings.introCash': 'Téléversez le logo. La prime s’encaisse en espèces au bureau. Pulse peut activer des wallets plus tard.',
    'settings.logo': 'Logo',
    'settings.noLogo': 'Aucun logo pour le moment.',
    'settings.wallets': 'Paiements wallet',
    'settings.walletsHint': 'Uniquement les rails activés par Pulse pour cette compagnie. Les clients envoient la prime à ces numéros. Espèces au bureau restent le défaut.',
    'settings.walletsDisabled': 'Aucun wallet n’est activé pour cette compagnie. Espèces au bureau par défaut. Pulse peut activer Wave, Orange Money, QMoney ou Pulse Pay.',
    'settings.walletsSaved': 'Paramètres de paiement enregistrés.',
    'settings.walletsFailed': 'Impossible d’enregistrer les paiements.',
    'modules.disabled': 'Ce module n’est pas activé pour votre compagnie. Demandez à Pulse Insurance de l’activer.',
    'settings.pulsePay': 'Numéro Pulse Pay',
    'settings.wave': 'Numéro Wave',
    'settings.orange': 'Orange Money',
    'settings.qmoney': 'QMoney',
    'settings.commission': 'Commission courtier %',
    'settings.saveWallets': 'Enregistrer les wallets',
    'settings.adminOnly': 'Seul l’admin IT de la compagnie peut téléverser le logo.',
    'settings.licenceNote': 'La licence et la facturation sont gérées par Pulse Insurance (super admin Pulse uniquement). Les admins IT gèrent logo, wallets et utilisateurs. Suspension ou clôture : Pulse uniquement.',
    'gra.loading': 'Chargement des déclarations...',
    'gra.intro': 'WMF (wakala), taxe GRA et prime émise style ICC pour {company}. Encaissements Pulse Pay / Wave vers {wave}.',
    'gra.downloadCsv': 'Télécharger CSV',
    'gra.printPack': 'Imprimer le dossier',
    'gra.restricted': 'Finance, audit, managers et admin IT peuvent ouvrir le dossier de fin de mois.',
    'gra.cbgNote': 'Règlement CBG 2005 : formulaire C (vie), D (non-vie), E/F (comptes et intermédiaires). Packs CSV/Excel issus du livre Pulse — pas un XML officiel inventé. Pack GRA ITAS à déposer ou ressaisir.',
    'gra.formC': 'CBG formulaire C (vie)',
    'gra.formD': 'CBG formulaire D (non-vie)',
    'gra.formE': 'CBG formulaire E (encaissements)',
    'gra.formF': 'CBG formulaire F (intermédiaires)',
    'gra.itas': 'Pack GRA ITAS',
    'gra.pulsePay': 'Encaissé Pulse Pay',
    'gra.claimsMonth': 'Sinistres du mois',
    'claims.loading': 'Chargement des sinistres...',
    'claims.intro': 'Déclarer un FNOL et faire évoluer le sinistre : ouvert → enquête → approuvé / rejeté / payé.',
    'claims.new': 'Nouveau sinistre',
    'claims.policySearch': 'Recherche de police',
    'claims.policySearchPh': 'Nom, plaque ou n° de police',
    'claims.pickPolicy': 'Choisir une police',
    'claims.incidentDate': 'Date du sinistre',
    'claims.place': 'Lieu',
    'claims.placePh': 'ex. carrefour Westfield',
    'claims.description': 'Que s’est-il passé',
    'claims.estimate': 'Montant estimé (GMD)',
    'claims.photos': 'Photos',
    'claims.photosHint': 'Facultatif. Si Storage n’est pas activé, le dossier est quand même créé.',
    'claims.file': 'Déclarer le sinistre',
    'claims.filed': 'Sinistre déclaré',
    'claims.fileFailed': 'Impossible de déclarer ce sinistre.',
    'claims.updateFailed': 'Impossible de mettre à jour le statut.',
    'claims.photoSkip': 'Photos ignorées (Storage indisponible).',
    'claims.id': 'N° sinistre',
    'claims.insured': 'Assuré',
    'claims.line': 'Branche',
    'claims.reserve': 'Provision',
    'claims.status': 'Statut',
    'claims.none': 'Aucun sinistre.',
    'claims.move': 'Passer à',
    'claims.st.open': 'ouvert',
    'claims.st.investigating': 'enquête',
    'claims.st.approved': 'approuvé',
    'claims.st.rejected': 'rejeté',
    'claims.st.paid': 'payé',
    'client.fnolTitle': 'Déclarer un sinistre (FNOL)',
    'client.fnolBlurb': 'Décrivez l’incident sur votre police. Photo facultative.',
    'client.fnolPolicy': 'Police',
    'client.fnolPick': 'Choisir une police',
    'client.fnolDesc': 'Que s’est-il passé',
    'client.fnolPhoto': 'Photo (facultatif)',
    'client.fnolSubmit': 'Envoyer la déclaration',
    'client.fnolFiled': 'Sinistre {num} déclaré. Le service sinistres vous contactera.',
    'client.fnolFailed': 'Impossible de déclarer ce sinistre.',
    'client.fnolPhotoSkip': 'Photo ignorée — la déclaration reste possible.',
    'ussd.title': 'Vérification USSD plaque / police',
    'ussd.intro': 'Démo *920# pour Africell, QCell et Gamcel. Un shortcode réel exige un contrat opérateur. Même index de plaques que la police.',
    'ussd.entryPh': 'Plaque ou n° de police',
    'ussd.send': 'Envoyer',
    'ussd.webhook': 'URL webhook opérateur',
    'ussd.webhookHint': 'POST style Africa’s Talking : sessionId, phoneNumber, text, serviceCode. En-tête x-webhook-secret (même secret que le webhook mobile money). Sans secret, rejeté.',
    'superadmin.language': 'Language / Langue',
    'superadmin.languageHelp': 'Personnel, espace client et vérification police de cette compagnie. Le portail super admin reste en anglais.',
    'superadmin.english': 'English',
    'superadmin.french': 'Français',
    'superadmin.languageSaved': 'Langue définie sur {lang} pour {company}. Le personnel et les clients la voient après rechargement.',
    'cert.org': 'Association des assureurs · Gambie',
    'cert.official': 'Copie numérique officielle · à présenter à la police sur demande',
    'cert.contact': 'Montrez ce certificat à la police. Ils scannent le QR pour vérifier.',
    'cert.plateNo': 'PLAQUE',
    'cert.type': 'TYPE',
    'cert.make': 'MARQUE',
    'cert.holder': 'TITULAIRE',
    'cert.nationalId': 'CNI',
    'cert.vehicle': 'VÉHICULE',
    'cert.vin': 'VIN',
    'cert.policyNo': 'POLICE N°',
    'cert.validFrom': 'VALIDITÉ',
    'cert.to': 'au',
    'cert.attestationNo': 'Attestation n° {num}',
    'cert.securityKey': 'CLÉ DE SÉCURITÉ',
    'cert.cover': 'GARANTIE',
    'cert.ecowas': 'CEDEAO',
    'cert.brownCard': 'Carte Brune incluse',
    'cert.dateOfIssue': 'DATE D’ÉMISSION {date}',
    'cert.attestationTitle': 'ATTESTATION D’ASSURANCE',
    'cert.coverConfirmed': 'COUVERTURE CONFIRMÉE',
    'cert.preparingQr': 'Préparation du QR…',
    'cert.plate': 'Plaque {plate}',
    'cert.expires': 'Expire {date}',
    'cert.expired': 'EXPIRÉ',
    'cert.valid': 'VALIDE',
    'cert.watermark': '{company} · GAMBIE',
    'cert.gambiaBand': '{mark} · GAMBIE'
};

const dictionaries = { en: EN, fr: FR };

function interpolate(str, vars) {
    if (!vars) return str;
    return String(str).replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ''));
}

export function translate(locale, key, vars) {
    const loc = normalizeLocale(locale);
    const dict = dictionaries[loc] || EN;
    return interpolate(dict[key] || EN[key] || key, vars);
}

const I18nContext = createContext({
    locale: 'en',
    setLocale: () => {},
    t: (key, vars) => translate('en', key, vars)
});

export function I18nProvider({ children }) {
    const [locale, setLocaleState] = useState('en');
    const setLocale = useCallback((next) => {
        setLocaleState(normalizeLocale(next));
    }, []);
    const t = useCallback((key, vars) => translate(locale, key, vars), [locale]);

    useEffect(() => {
        document.documentElement.lang = locale;
    }, [locale]);

    const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    return useContext(I18nContext);
}

export async function loadPublicLocale(tenantId) {
    const id = tenantId || TENANT_ID;
    try {
        const snap = await getDoc(doc(db, 'directory', id));
        if (snap.exists()) return normalizeLocale(snap.data().locale);
    } catch (e) { /* directory is public; ignore offline */ }
    return 'en';
}

export function usePublicTenantLocale(tenantId) {
    const { setLocale } = useI18n();
    useEffect(() => {
        let live = true;
        loadPublicLocale(tenantId).then((loc) => {
            if (live) setLocale(loc);
        });
        return () => { live = false; };
    }, [tenantId, setLocale]);
}

export function useStaffTenantLocale(tenantId) {
    const { setLocale } = useI18n();
    useEffect(() => {
        if (!tenantId) return undefined;
        let live = true;
        getDoc(doc(db, 'tenants', tenantId)).then((snap) => {
            if (live && snap.exists()) setLocale(snap.data().locale);
        }).catch(() => {});
        return () => { live = false; };
    }, [tenantId, setLocale]);
}

export function brandMark(companyName) {
    const name = String(companyName || 'Pulse Insurance').trim();
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 3).toUpperCase() || 'PI';
}
